import React from 'react';
import { MapPin, Phone, Mail, Clock, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { analyticsService } from '../services/analyticsService';

interface BusinessFooterProps {
  variant?: 'default' | 'minimal';
  showLanguageSwitcher?: boolean;
}

const BusinessFooter: React.FC<BusinessFooterProps> = ({ 
  variant = 'default',
  showLanguageSwitcher = true 
}) => {
  const { t } = useLanguage();
  
  // Business NAP information - consistent across all pages
  const businessInfo = {
    name: 'Cotizatrin',
    address: 'Tallinn, Estonia',
    phone: '',
    email: 'info_cotizatrin@autotrin.com',
    website: 'www.cotizatrin.com'
  };

  if (variant === 'minimal') {
    return (
      <footer className="bg-primary text-white py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* NAP Information - Minimal */}
            <div className="text-center md:text-left mb-3 md:mb-0">
              <p className="font-semibold">{businessInfo.name}</p>
              <p className="text-sm text-gray-300">{businessInfo.email}</p>
            </div>

            <div className="text-center text-sm">
              <p>© 2025 {businessInfo.name}. {t('common.copyright')}</p>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-primary text-white py-6">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Company Information */}
          <div>
            <h3 className="font-bold text-lg mb-3">{businessInfo.name}</h3>
            <p className="text-gray-300 text-sm mb-4">{t('business.description')}</p>
            
            {/* NAP Information */}
            <div className="space-y-2">
              <div className="flex items-start text-sm">
                <MapPin size={16} className="text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                <span className="text-gray-300">{businessInfo.address}</span>
              </div>

              {businessInfo.phone && (
                <div className="flex items-center text-sm">
                  <Phone size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                  <a
                    href={`tel:${businessInfo.phone.replace(/\s+/g, '')}`}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {businessInfo.phone}
                  </a>
                </div>
              )}

              <div className="flex items-center text-sm">
                <Mail size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                <a
                  href={`mailto:${businessInfo.email}`}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {businessInfo.email}
                </a>
              </div>

              <div className="flex items-center text-sm">
                <Globe size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-300">{businessInfo.website}</span>
              </div>
            </div>
          </div>
          
          {/* Services */}
          <div>
            <h3 className="font-bold text-lg mb-3">{t('business.services.title')}</h3>
            <ul className="text-gray-300 text-sm space-y-1">
              <li>• {t('business.services.quotation')}</li>
              <li>• {t('business.services.tolls')}</li>
              <li>• {t('business.services.routes')}</li>
              <li>• {t('business.services.security')}</li>
              <li>• {t('business.services.reports')}</li>
            </ul>
          </div>
          
          {/* Business Hours & Links */}
          <div>
            <h3 className="font-bold text-lg mb-3">{t('business.hours.title')}</h3>
            <div className="text-gray-300 text-sm space-y-1 mb-4">
              <div className="flex items-center">
                <Clock size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                <span>{t('business.hours.weekdays')}</span>
              </div>
              <div className="flex items-center">
                <Clock size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                <span>{t('business.hours.saturday')}</span>
              </div>
              <div className="flex items-center">
                <Clock size={16} className="text-gray-400 mr-2 flex-shrink-0" />
                <span>{t('business.hours.sunday')}</span>
              </div>
            </div>
            
            {/* Legal Links */}
            <div className="flex flex-wrap gap-2">
              <a 
                href="/terminos-y-condiciones" 
                onClick={(e) => {
                  e.preventDefault();
                  window.location.hash = 'terminos-y-condiciones';
                  analyticsService.trackCustomEvent('terms_click');
                }}
                className="text-sm text-gray-300 hover:text-white underline"
              >
                {t('legal.termsConditions')}
              </a>
              <span className="text-gray-500">|</span>
              <a 
                href="/aviso-de-privacidad" 
                onClick={(e) => {
                  e.preventDefault();
                  window.location.hash = 'aviso-de-privacidad';
                  analyticsService.trackCustomEvent('privacy_click');
                }}
                className="text-sm text-gray-300 hover:text-white underline"
              >
                {t('legal.privacyPolicy')}
              </a>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-primary-light pt-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-center md:text-left mb-3 md:mb-0">
              <p className="text-sm">© 2025 {businessInfo.name}. {t('common.copyright')}</p>
            </div>
            
            {showLanguageSwitcher && (
              <div>
                <LanguageSwitcher variant="footer" />
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default BusinessFooter;